import { defineStore } from "pinia";
import { computed, ref } from "vue";
import IChat from "@/types/IChat";
import IMessage from "@/types/IMessage";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useCoachStore } from "@/store/coach";
import { ChatTypesEnum } from "@/enums/ChatTypes";
import { CoachingTypesEnum } from "@/enums/CoachingTypes";
import { useSupportChat } from "@/composables/useSupportChat";

dayjs.extend(customParseFormat);

export const useChatStore = defineStore(
  "chat",
  () => {
    const coachStore = useCoachStore();
    const isCoach = computed(
      (): boolean =>
        !!(
          coachStore &&
          Object.keys(coachStore.data).length &&
          coachStore.coachAccessToken
        )
    );
    const initValue: IChat[] = [
      {
        _id: "workout",
        type: ChatTypesEnum.Workout,
        status: "closed",
        isJoined: false,
        userInfo: {
          _id: null,
          fullName: ChatTypesEnum.Workout,
          email: "",
          coachId: null,
          coachingType: CoachingTypesEnum.NoCoaching,
        },
        coachInfo: {
          _id: null,
          fullName: null,
          email: "",
        },
        lastMessage: null,
        lastMessageAt: null,
        unreadCount: 0,
        isAccessible: false,
      },
      {
        _id: "nutrition",
        type: ChatTypesEnum.Nutrition,
        status: "closed",
        isJoined: false,
        userInfo: {
          _id: null,
          fullName: ChatTypesEnum.Nutrition,
          email: "",
          coachId: null,
          coachingType: CoachingTypesEnum.NoCoaching,
        },
        coachInfo: {
          _id: null,
          fullName: null,
          email: "",
        },
        lastMessage: null,
        lastMessageAt: null,
        unreadCount: 0,
        isAccessible: false,
      },
      {
        _id: "app",
        type: ChatTypesEnum.App,
        status: "closed",
        isJoined: false,
        userInfo: {
          _id: null,
          fullName: ChatTypesEnum.App,
          email: "",
          coachId: null,
          coachingType: CoachingTypesEnum.NoCoaching,
        },
        coachInfo: {
          _id: null,
          fullName: null,
          email: "",
        },
        lastMessage: null,
        lastMessageAt: null,
        unreadCount: 0,
        isAccessible: false,
      },
    ];

    const data = ref<IChat[]>(isCoach.value ? [] : [...initValue]);

    const countUnreadMessage = computed((): number => {
      return data.value.reduce((acc, chat) => acc + chat.unreadCount, 0);
    });

    const { currentUserName } = useSupportChat();

    const isJoinFlag = ref(false);

    function setInitData() {
      data.value = [...initValue];
    }

    function clearData() {
      data.value = [];
    }

    function sortArrayFn(
      arr: IChat[],
      desc = true,
      sortByProp = "lastMessageAt"
    ): IChat[] {
      return [...arr]?.sort((a: IChat, b: IChat) => {
        const aZero = a.unreadCount === 0;
        const bZero = b.unreadCount === 0;
        if (aZero !== bZero) {
          return aZero ? 1 : -1;
        }

        return dayjs(b.lastMessageAt).unix() - dayjs(a.lastMessageAt).unix();
      });
    }

    function getChatIndexById(chatId: string): number {
      return data.value.findIndex((item) => item._id === chatId);
    }

    function deleteDataById(chatId: string): void {
      const idx = getChatIndexById(chatId);
      if (idx >= 0) {
        data.value.splice(idx, 1);
      }
    }

    function setData(payload: IChat[]): void {
      data.value = payload;
    }

    function joinData(payload: IChat[]): void {
      const existingIds = new Set(data.value.map((chat) => chat._id));

      const uniqueNewChats = payload.filter(
        (chat) => !existingIds.has(chat._id)
      );

      data.value = [...data.value, ...uniqueNewChats];
    }

    function joinDataOld(payload: IChat[]): void {
      data.value = [...data.value, ...payload];
    }

    function updateDataById(payload: { data: IMessage; type: string }): void {
      const idx = getChatIndexById(payload.data.chatId);
      if (idx >= 0) {
        data.value[idx].lastMessage = payload.data.isDeleted
          ? "Deleted message"
          : payload.data.message || "new file";
        data.value[idx].lastMessageAt = payload.data.createdAt;

        if (
          payload.type === "inbox" &&
          payload.data.fullName !== currentUserName.value &&
          ["common", "system_auto_reply", "system_welcome"].includes(
            payload.data.type
          )
        ) {
          data.value[idx].unreadCount = data.value[idx].unreadCount + 1;
        }
      }
    }

    function decreaseMessage(chatId: string): void {
      const idx = getChatIndexById(chatId);
      if (idx >= 0 && data.value[idx].unreadCount > 0) {
        data.value[idx].unreadCount = data.value[idx].unreadCount - 1;
      }
    }

    return {
      setData,
      joinData,
      setInitData,
      clearData,
      updateDataById,
      decreaseMessage,
      deleteDataById,
      getChatIndexById,
      countUnreadMessage,
      data,
      isJoinFlag,
    };
  },
  {
    persist: false,
  }
);
